import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, ArrowUp, ArrowDown, Plus, Trash2, RotateCcw, FolderPlus, FolderTree,
  LayoutDashboard, Building2, Users, Activity, Package, Target, Truck, FileText, Megaphone, Link as LinkIcon, Star, Bookmark, CalendarDays, PhoneCall, ShoppingCart, Factory, Inbox, Mailbox, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  LayoutDashboard, Building2, Users, Activity, Package, Target, Truck, FileText, Megaphone, Link: LinkIcon, Star, Bookmark, CalendarDays, PhoneCall, ShoppingCart, Factory, Inbox, Mailbox, FolderTree, Sparkles,
};

// Suggesties voor snelkoppelingen (saved filters/views)
const SHORTCUT_PRESETS = [
  { label: "Klanten in Amsterdam", to: "/organisaties?status=klant&q=Amsterdam", icon: "Building2" },
  { label: "Kansen in Onderhandeling", to: "/verkoopkansen?fase=Onderhandeling", icon: "Target" },
  { label: "Facturen wachtrij", to: "/verkoopfacturen?tab=wachtrij", icon: "FileText" },
  { label: "Vervallen facturen", to: "/verkoopfacturen?tab=vervallen", icon: "FileText" },
  { label: "Open taken vandaag", to: "/activiteiten?status=open", icon: "Activity" },
  { label: "Producten onder minimum", to: "/producten?onder=1", icon: "Package" },
];

function NieuweSnelkoppelingDialog({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ label: "", to: "", icon: "Star" });
  const submit = () => {
    if (!f.label.trim() || !f.to.trim()) { toast.error("Label en URL zijn verplicht"); return; }
    onAdd({ ...f, id: `custom-${Date.now()}`, visible: true, custom: true });
    setF({ label: "", to: "", icon: "Star" });
    setOpen(false);
  };
  const applyPreset = (p) => setF({ label: p.label, to: p.to, icon: p.icon });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="add-shortcut-btn">
          <Plus className="h-4 w-4 mr-1" /> Snelkoppeling toevoegen
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nieuwe snelkoppeling</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Voorbeelden</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {SHORTCUT_PRESETS.map(p => (
                <button key={p.label} type="button" onClick={() => applyPreset(p)}
                  className="text-xs border border-border rounded-full px-2.5 py-1 hover:bg-secondary transition-colors"
                  data-testid={`preset-${p.label}`}>{p.label}</button>
              ))}
            </div>
          </div>
          <div><Label>Label</Label><Input value={f.label} onChange={(e)=>setF({...f, label: e.target.value})} placeholder="Bijv. Mijn favorieten" data-testid="sc-label" /></div>
          <div><Label>URL / pad</Label><Input value={f.to} onChange={(e)=>setF({...f, to: e.target.value})} placeholder="/verkoopkansen?fase=Onderhandeling" data-testid="sc-to" />
            <div className="text-xs text-muted-foreground mt-1">Begin met "/" voor een interne route, of "https://" voor externe URL.</div>
          </div>
          <div><Label>Icoon</Label>
            <Select value={f.icon} onValueChange={(v)=>setF({...f, icon: v})}>
              <SelectTrigger data-testid="sc-icon"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(ICONS).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="sc-submit">Toevoegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NieuwSubmenuDialog({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ label: "", icon: "FolderTree" });
  const submit = () => {
    if (!f.label.trim()) { toast.error("Naam is verplicht"); return; }
    onAdd({ id: `group-${Date.now()}`, label: f.label.trim(), icon: f.icon, to: "#", visible: true, custom: true, is_group: true, parent: null });
    setF({ label: "", icon: "FolderTree" });
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="add-group-btn">
          <FolderPlus className="h-4 w-4 mr-1" /> Submenu toevoegen
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nieuw submenu</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Naam van het submenu</Label><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Bijv. Verkoop" data-testid="group-label" /></div>
          <div><Label>Icoon</Label>
            <Select value={f.icon} onValueChange={(v) => setF({ ...f, icon: v })}>
              <SelectTrigger data-testid="group-icon"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(ICONS).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">Na het aanmaken kun je bij elk menu-item kiezen om het onder dit submenu te plaatsen.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="group-submit">Aanmaken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InstellingenMenu() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/menu");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put("/menu", { items: next });
      setItems(next);
      // Broadcast menu update for Layout to refresh
      window.dispatchEvent(new Event("menu-updated"));
      toast.success("Menu opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const toggleVisible = (id) => save(items.map(it => it.id === id ? { ...it, visible: !it.visible } : it));
  const move = (id, dir) => {
    const cur = items.find(it => it.id === id);
    if (!cur) return;
    const scopeKey = cur.parent || null;
    // siblings = items met dezelfde parent (in lijstvolgorde)
    const scope = items.map((it, idx) => ({ it, idx })).filter(x => (x.it.parent || null) === scopeKey);
    const pos = scope.findIndex(x => x.it.id === id);
    const npos = pos + dir;
    if (npos < 0 || npos >= scope.length) return;
    const a = scope[pos].idx, b = scope[npos].idx;
    const next = [...items];
    [next[a], next[b]] = [next[b], next[a]];
    save(next);
  };
  const setParent = (id, parentId) => save(items.map(it => it.id === id ? { ...it, parent: parentId || null } : it));
  const removeItem = (id) => {
    // bij verwijderen van een submenu: koppel kinderen weer los
    const next = items.filter(it => it.id !== id).map(it => it.parent === id ? { ...it, parent: null } : it);
    save(next);
  };
  const addShortcut = (sc) => save([...items, sc]);
  const addGroup = (g) => save([...items, g]);
  const reset = async () => {
    if (!window.confirm("Menu terugzetten naar standaard?")) return;
    await api.delete("/menu");
    await load();
    window.dispatchEvent(new Event("menu-updated"));
    toast.success("Menu hersteld");
  };

  const Icon = (name) => {
    const C = ICONS[name] || LinkIcon;
    return <C className="h-4 w-4 text-muted-foreground" />;
  };

  const zichtbaar = items.filter(i => i.visible).length;

  // Submenu-groepen en weergavevolgorde (kinderen direct onder hun groep, ingesprongen)
  const groups = items.filter(i => i.is_group);
  const groupIds = new Set(groups.map(g => g.id));
  const displayRows = [];
  items.filter(i => !i.parent || !groupIds.has(i.parent)).forEach(it => {
    displayRows.push({ it, depth: 0 });
    if (it.is_group) {
      items.filter(c => c.parent === it.id && !c.is_group).forEach(c => displayRows.push({ it: c, depth: 1 }));
    }
  });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="menu-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Terug
        </Button>
        <div className="flex items-center gap-2">
          <NieuwSubmenuDialog onAdd={addGroup} />
          <NieuweSnelkoppelingDialog onAdd={addShortcut} />
          <Button variant="outline" onClick={reset} data-testid="reset-menu">
            <RotateCcw className="h-4 w-4 mr-1" /> Standaard herstellen
          </Button>
        </div>
      </div>

      <div>
        <div className="text-sm text-muted-foreground">Instellingen</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Menu aanpassen</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {zichtbaar} van {items.length} items zichtbaar. Gebruik de pijlen om te ordenen, verberg items, of plaats ze onder een submenu.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl divide-y divide-border">
        {displayRows.map(({ it, depth }) => {
          const isGroup = !!it.is_group;
          return (
          <div key={it.id} className={`p-3 flex items-center gap-3 ${depth ? "pl-10 bg-secondary/20" : ""}`} data-testid={`menu-item-${it.id}`}>
            <div className="flex flex-col gap-0.5">
              <Button size="icon" variant="ghost" className="h-6 w-6"
                onClick={() => move(it.id, -1)} data-testid={`up-${it.id}`}>
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6"
                onClick={() => move(it.id, 1)} data-testid={`down-${it.id}`}>
                <ArrowDown className="h-3 w-3" />
              </Button>
            </div>
            <div className="shrink-0">{Icon(it.icon)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">
                {it.label}
                {isGroup && <span className="text-[10px] uppercase tracking-wide bg-primary/15 text-primary rounded px-1.5 py-0.5">Submenu</span>}
                {it.custom && !isGroup && <span className="text-[10px] uppercase tracking-wide bg-accent/15 text-accent rounded px-1.5 py-0.5">Snelkoppeling</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate font-mono">{isGroup ? "uitklapbaar menu" : it.to}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!isGroup && groups.length > 0 && (
                <Select value={it.parent || "none"} onValueChange={(v) => setParent(it.id, v === "none" ? null : v)}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid={`parent-${it.id}`}><SelectValue placeholder="Submenu" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Hoofdmenu —</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Switch checked={!!it.visible} onCheckedChange={() => toggleVisible(it.id)}
                      data-testid={`toggle-${it.id}`} />
              {it.custom && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                        onClick={() => removeItem(it.id)} data-testid={`delete-${it.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          );
        })}
        {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Menu leeg.</div>}
      </div>

      {saving && <div className="text-xs text-muted-foreground">Opslaan…</div>}
    </div>
  );
}
